/*
 * main-reviews.js — the store-wide /reviews page.
 *
 * Consumes the store-wide list endpoint (Piece 1):
 *   GET /apps/dashboard/reviews?page=&sort=&subbrand=&product=
 *                              &min_rating=&max_rating=
 *   → { reviews:[...], page, page_size, total }
 *
 * All filtering/sorting is SERVER-SIDE: every chip/sort change re-fetches
 * page 1 and replaces the grid; "Load more" fetches the next page and
 * appends. Reuses the SAME review-card vocabulary as the PDP widget.
 *
 * If the URL carries ?product=<gid> (a PDP "View all" click), the page
 * opens pre-scoped to that product.
 *
 * Dual empty states (find-a-store discipline), both distinct from error:
 *   pre-launch → no reviews exist at all (the launch default)
 *   filtered   → reviews exist, none match the active filters (+ clear)
 *
 * XSS: review body/title/name/location are user-submitted; every
 * interpolated string is escapeHtml()'d before it touches innerHTML.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-reviews-page]');
  if (!root) return;

  var BASE = '/apps/dashboard/reviews';

  function $(sel) { return root.querySelector(sel); }
  function $$(sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }

  var filtersEl   = $('[data-reviews-filters]');
  var summaryEl   = $('[data-reviews-summary]');
  var gridEl      = $('[data-reviews-grid]');
  var loadingEl   = $('[data-reviews-loading]');
  var loadMoreWrap = $('[data-reviews-loadmore-wrap]');
  var loadMoreBtn = $('[data-reviews-loadmore]');
  var prelaunchEl = $('[data-reviews-empty-prelaunch]');
  var filteredEl  = $('[data-reviews-empty-filtered]');
  var errorEl     = $('[data-reviews-error]');
  var productNote = $('[data-reviews-product-note]');
  var subBtns     = $$('[data-reviews-sub]');
  var ratingBtns  = $$('[data-reviews-rating]');
  var sortEl      = $('[data-reviews-sort]');
  var clearBtns   = $$('[data-reviews-clear]');

  // Pre-apply ?product= from the URL (PDP "View all" deep link). Strictly
  // null unless a real, non-empty GID is present — a missing param,
  // '?product=', or whitespace must NOT count as an active product filter
  // (otherwise a bare load wrongly shows the deep-link scoped UI).
  var initialProduct = null;
  try {
    var rawProduct = new URLSearchParams(window.location.search).get('product');
    initialProduct = rawProduct && rawProduct.trim() ? rawProduct.trim() : null;
  } catch (e) { initialProduct = null; }

  var state = {
    sort: 'recent',
    subbrand: null,
    product: initialProduct,
    minRating: null,
    maxRating: null,
    page: 1,
    total: 0,
    loaded: 0,
  };

  // Request sequencer — rapid filter/sort clicks fire overlapping load()s that
  // can resolve out of order; every load() captures a token and only the newest
  // is allowed to render (mirrors search-everything.js's seq guard).
  var loadSeq = 0;

  // ─── Escape helper (XSS-safe text insertion) ─────────────────────
  function escapeHtml(s) {
    if (s === null || s === undefined) return '';
    return String(s).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  function starGlyphs(rating) {
    var n = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
    var out = '';
    for (var i = 0; i < 5; i++) out += i < n ? '★' : '☆';
    return out;
  }

  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  function fmtDate(iso) {
    if (!iso) return '';
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return MONTHS[d.getUTCMonth()] + ' ' + d.getUTCDate() + ', ' + d.getUTCFullYear();
  }

  function anyFilterActive() {
    return !!(state.subbrand || state.product || state.minRating || state.maxRating);
  }

  // Media strip → lightbox (identical to the PDP widget). url is our derived
  // public-bucket URL; escapeHtml'd in the attribute, read back via the data
  // attribute and set as .src (never innerHTML).
  function mediaStripHtml(media) {
    if (!Array.isArray(media) || media.length === 0) return '';
    return '<div class="pr-card-media">' + media.map(function (m) {
      var u = escapeHtml(m.url);
      if (m.media_type === 'video') {
        return '<button type="button" class="pr-media-thumb is-video" data-pr-lightbox data-media-type="video" data-media-url="' + u + '" aria-label="Play video">▶</button>';
      }
      return '<button type="button" class="pr-media-thumb" data-pr-lightbox data-media-type="image" data-media-url="' + u + '" aria-label="View photo"><img src="' + u + '" alt="" loading="lazy"></button>';
    }).join('') + '</div>';
  }

  // Admin response — "Response from Circadia" sub-block, shown only when the
  // review carries one (projection: r.response = { response_body, responded_at }).
  // Identical vocabulary to the PDP widget. response_body is admin-entered →
  // escapeHtml'd like every other interpolated string.
  function responseHtml(r) {
    if (!r.response || !r.response.response_body) return '';
    return (
      '<div class="pr-response">' +
        '<div class="pr-response-label">Response from Circadia</div>' +
        '<p class="pr-response-body">' + escapeHtml(r.response.response_body) + '</p>' +
        '<div class="pr-response-date">' + escapeHtml(fmtDate(r.response.responded_at)) + '</div>' +
      '</div>'
    );
  }

  // ─── Card — identical vocabulary to the PDP widget ───────────────
  function cardHtml(r) {
    var titleHtml = r.title
      ? '<h4 class="pr-review-title">' + escapeHtml(r.title) + '</h4>'
      : '';
    var locHtml = r.reviewer_location
      ? '<div class="review-loc">' + escapeHtml(r.reviewer_location) + '</div>'
      : '';
    var verifiedHtml = r.verified_purchase
      ? '<span class="pr-verified">✓ Verified purchase</span>'
      : '';
    var editedHtml = r.edited ? ' <span class="pr-edited">(edited)</span>' : '';
    var count = Number(r.helpful_count) || 0;
    return (
      '<article class="review-card pr-card">' +
        '<div class="pr-card-top">' +
          '<div class="review-stars" aria-hidden="true">' + starGlyphs(r.rating) + '</div>' +
          '<span class="pr-date">' + escapeHtml(fmtDate(r.created_at)) + editedHtml + '</span>' +
        '</div>' +
        '<div class="pr-card-body">' +
          titleHtml +
          '<p class="review-quote">“' + escapeHtml(r.body) + '”</p>' +
        '</div>' +
        mediaStripHtml(r.media) +
        responseHtml(r) +
        '<div class="review-foot">' +
          '<div>' +
            '<div class="review-author">' + escapeHtml(r.reviewer_name) + '</div>' +
            locHtml +
            verifiedHtml +
          '</div>' +
          '<button type="button" class="pr-helpful" data-helpful data-review-id="' + escapeHtml(r.id) +
            '" data-base-count="' + count + '" data-voted="false">' +
            'Helpful (' + count + ')' +
          '</button>' +
        '</div>' +
      '</article>'
    );
  }

  // ─── Helpful vote (optimistic, dedup-safe, no double-count) ──────
  function setHelpfulLabel(btn, n) { btn.textContent = 'Helpful (' + n + ')'; }

  function onVote(btn) {
    if (btn.disabled || btn.getAttribute('data-voted') === 'true') return;
    var id = btn.getAttribute('data-review-id');
    if (!id) return;
    var base = Number(btn.getAttribute('data-base-count')) || 0;

    btn.disabled = true;
    btn.setAttribute('data-voted', 'true');
    setHelpfulLabel(btn, base + 1);

    fetch(BASE + '/' + encodeURIComponent(id) + '/helpful', {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin',
    })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (body) {
        if (body && body.already_voted === true) {
          setHelpfulLabel(btn, base); // roll back — never over-count
        }
      })
      .catch(function () {
        setHelpfulLabel(btn, base);
        btn.setAttribute('data-voted', 'false');
        btn.disabled = false;
      });
  }

  if (gridEl) {
    gridEl.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('[data-helpful]') : null;
      if (btn && gridEl.contains(btn)) onVote(btn);
      var media = e.target.closest ? e.target.closest('[data-pr-lightbox]') : null;
      if (media && gridEl.contains(media)) {
        openLightbox(media.getAttribute('data-media-url'), media.getAttribute('data-media-type'));
      }
    });
  }

  // ─── Lightbox (one shared overlay; opens full image / playable video) ──
  var lightboxEl = null;
  function closeLightbox() {
    if (!lightboxEl) return;
    lightboxEl.hidden = true;
    lightboxEl.innerHTML = ''; // stops any playing video
  }
  function openLightbox(mediaUrl, type) {
    if (!mediaUrl) return;
    if (!lightboxEl) {
      lightboxEl = document.createElement('div');
      lightboxEl.className = 'pr-lightbox';
      lightboxEl.hidden = true;
      lightboxEl.addEventListener('click', closeLightbox); // backdrop closes
      document.body.appendChild(lightboxEl);
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') closeLightbox();
      });
    }
    lightboxEl.innerHTML = '';
    var el;
    if (type === 'video') {
      el = document.createElement('video');
      el.controls = true;
      el.autoplay = true;
      el.playsInline = true;
    } else {
      el = document.createElement('img');
      el.alt = '';
    }
    el.className = 'pr-lightbox-media';
    el.src = mediaUrl; // .src property — never innerHTML
    el.addEventListener('click', function (e) { e.stopPropagation(); });
    lightboxEl.appendChild(el);
    lightboxEl.hidden = false;
  }

  // ─── View state ──────────────────────────────────────────────────
  function setView(view) {
    if (loadingEl)   loadingEl.hidden   = view !== 'loading';
    if (prelaunchEl) prelaunchEl.hidden = view !== 'prelaunch';
    if (filteredEl)  filteredEl.hidden  = view !== 'filtered';
    if (errorEl)     errorEl.hidden     = view !== 'error';
    var isResults = view === 'results';
    if (summaryEl) summaryEl.hidden = !isResults;
    if (gridEl)    gridEl.hidden    = !isResults;
    if (loadMoreWrap) loadMoreWrap.hidden = !isResults || state.loaded >= state.total;
    // The filter bar is meaningless pre-launch; otherwise it stays visible
    // (kept up during loading/results/filtered so re-fetches don't flash it).
    if (filtersEl) filtersEl.hidden = view === 'prelaunch';
    if (productNote) productNote.hidden = !state.product;
  }

  function renderSummary() {
    if (!summaryEl) return;
    var label = state.total === 1 ? 'review' : 'reviews';
    summaryEl.textContent =
      (anyFilterActive() ? state.total + ' matching ' + label : state.total + ' ' + label);
  }

  // ─── Query builder → endpoint params ─────────────────────────────
  function buildQuery() {
    var p = new URLSearchParams();
    p.set('page', String(state.page));
    p.set('sort', state.sort);
    if (state.subbrand) p.set('subbrand', state.subbrand);
    if (state.product) p.set('product', state.product);
    if (state.minRating) p.set('min_rating', String(state.minRating));
    if (state.maxRating) p.set('max_rating', String(state.maxRating));
    return p.toString();
  }

  // ─── Fetch + render ──────────────────────────────────────────────
  // reset = true → filter/sort change (page 1, replace). false → load more.
  function load(reset) {
    if (reset) {
      state.page = 1;
      state.loaded = 0;
      setView('loading');
    } else if (loadMoreBtn) {
      loadMoreBtn.disabled = true;
      loadMoreBtn.textContent = 'Loading…';
    }

    var mySeq = ++loadSeq;
    fetch(BASE + '?' + buildQuery(), {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' },
    })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (body) {
        if (mySeq !== loadSeq) return; // a newer load() superseded this one
        var list = (body && Array.isArray(body.reviews)) ? body.reviews : [];
        state.total = Number(body && body.total) || 0;

        if (reset) gridEl.innerHTML = '';
        gridEl.insertAdjacentHTML('beforeend', list.map(cardHtml).join(''));
        state.loaded += list.length;

        if (state.total === 0) {
          // Dual empty states — distinct from the error view.
          setView(anyFilterActive() ? 'filtered' : 'prelaunch');
          return;
        }
        renderSummary();
        setView('results');
        if (loadMoreBtn) {
          loadMoreBtn.disabled = false;
          loadMoreBtn.textContent = 'Load more reviews';
        }
      })
      .catch(function (err) {
        if (mySeq !== loadSeq) return; // stale failure — a newer load() owns the view
        if (reset) {
          setView('error');
        } else if (loadMoreBtn) {
          // Load-more failure: keep what's shown, let them retry.
          loadMoreBtn.disabled = false;
          loadMoreBtn.textContent = 'Load more reviews';
        }
        console.error('[reviews] load failed:', err);
      });
  }

  // ─── Wiring ──────────────────────────────────────────────────────
  function activate(buttons, btn) {
    buttons.forEach(function (b) { b.classList.remove('active'); });
    btn.classList.add('active');
  }

  subBtns.forEach(function (btn) {
    btn.addEventListener('click', function () {
      activate(subBtns, btn);
      var v = btn.getAttribute('data-reviews-sub');
      state.subbrand = v === 'all' ? null : v; // 'rise' | 'drift'
      load(true);
    });
  });

  ratingBtns.forEach(function (btn) {
    btn.addEventListener('click', function () {
      activate(ratingBtns, btn);
      var v = btn.getAttribute('data-reviews-rating');
      // Map the rating buckets → min_rating/max_rating. Only the 1-2★
      // bucket needs BOTH bounds; the "N+" buckets are a floor only.
      if (v === '5') { state.minRating = 5; state.maxRating = null; }
      else if (v === '4plus') { state.minRating = 4; state.maxRating = null; }
      else if (v === '3plus') { state.minRating = 3; state.maxRating = null; }
      else if (v === 'low') { state.minRating = 1; state.maxRating = 2; }
      else { state.minRating = null; state.maxRating = null; } // 'all'
      load(true);
    });
  });

  if (sortEl) {
    sortEl.addEventListener('change', function () {
      state.sort = sortEl.value || 'recent';
      load(true);
    });
  }

  if (loadMoreBtn) {
    loadMoreBtn.addEventListener('click', function () {
      state.page += 1;
      load(false);
    });
  }

  // "Clear filters" (filtered-empty state) AND "show all" (product note)
  // both reset every filter, including the deep-linked product.
  function resetAllFilters() {
    state.subbrand = null;
    state.product = null;
    state.minRating = null;
    state.maxRating = null;
    state.sort = 'recent';
    if (sortEl) sortEl.value = 'recent';
    subBtns.forEach(function (b) {
      b.classList.toggle('active', b.getAttribute('data-reviews-sub') === 'all');
    });
    ratingBtns.forEach(function (b) {
      b.classList.toggle('active', b.getAttribute('data-reviews-rating') === 'all');
    });
    load(true);
  }
  clearBtns.forEach(function (b) {
    b.addEventListener('click', resetAllFilters);
  });

  load(true);
})();
